/**
 * InviteModal
 *
 * Inviting someone to Dō by email, opened from the profile menu's
 * "Invite to Dō" row. Everything but the drawing is shared with mobile in
 * `shared/src/utils/invitation`: the send and how its answer is read
 * (`send-invitation`), the form's state (`invite-form`) and the quota's words
 * (`invitation-quota`). The drawing is `InviteModalView`.
 *
 * Every account gets five invitations and spent ones never come back, so the
 * dialog shows what is left, and with none left it opens straight to an
 * all-sent state with no field rather than a form that can only fail.
 */

import { useCallback, useEffect, useState } from 'react';

import { APP_INVITATION_COPY } from '@personal-trainer-app/shared/src/config/app-invitation.config';
import { invitationsLeft } from '@personal-trainer-app/shared/src/utils/invitation/invitation-quota';
import {
  applyInviteOutcome,
  editInviteEmail,
  INVITE_FORM_DEFAULTS,
  startInviteSend,
} from '@personal-trainer-app/shared/src/utils/invitation/invite-form';
import { sendAppInvitation } from '@personal-trainer-app/shared/src/utils/invitation/send-invitation';

import { InviteModalView } from './InviteModalView';

import type { InviteModalProps } from './InviteModal.types';

import { useToast } from '@/components/ui/Toast';
import { useAuthStore } from '@/stores';

export function InviteModal({
  isOpen,
  onClose,
}: InviteModalProps): React.JSX.Element {
  const toast = useToast();
  const username = useAuthStore((state) => state.user?.username ?? '');
  const left = useAuthStore((state) => invitationsLeft(state.user));
  const [form, setForm] = useState(INVITE_FORM_DEFAULTS);

  // Reopening should not resurrect the last attempt or its error.
  useEffect(() => {
    if (isOpen) setForm(INVITE_FORM_DEFAULTS);
  }, [isOpen]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const email = event.target.value;
      setForm((current) => editInviteEmail(current, email));
    },
    []
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setForm(startInviteSend);

      const outcome = await sendAppInvitation(form.email);
      setForm((current) => applyInviteOutcome(current, outcome));

      if (outcome.kind === 'sent') {
        toast.success(APP_INVITATION_COPY.sentBody(outcome.email), {
          title: APP_INVITATION_COPY.sentTitle,
        });
        onClose();
      }
    },
    [form.email, toast, onClose]
  );

  return (
    <InviteModalView
      isOpen={isOpen}
      onClose={onClose}
      username={username}
      left={left}
      form={form}
      onEmailChange={handleChange}
      onSubmit={(event) => void handleSubmit(event)}
    />
  );
}

export default InviteModal;
